// Porta de entrada do editor: o médico digita o código de 6 dígitos que o celular
// mostrou e resgata a sessão (POST /api/pair). Distingue os quatro estados de falha
// da rota — "ainda processando" (tentar de novo) de "expirado/errado/limite".
// (O pixel fino do mockup 1f-entrada-codigo fica como acabamento posterior.)

import { useState, type FormEvent } from 'react';

import { parear } from '../../api/client';
import { btnPrimario, cor, fonte } from '../../styles/tokens';

interface Props {
  onPareado: (sessionId: string, codigo: string) => void;
  aviso?: string; // mensagem vinda do editor (ex.: sessão expirou durante a edição)
}

export function Pareamento({ onPareado, aviso }: Props): JSX.Element {
  const [code, setCode] = useState('');
  const [erro, setErro] = useState<string | null>(aviso ?? null);
  const [carregando, setCarregando] = useState(false);

  const valido = /^\d{6}$/.test(code);

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!valido || carregando) return;
    setCarregando(true);
    setErro(null);
    const r = await parear(code);
    setCarregando(false);
    switch (r.status) {
      case 'ok':
        onPareado(r.sessionId, code);
        return;
      case 'processando':
        setErro('O rascunho ainda está sendo gerado. Aguarde alguns segundos e tente de novo.');
        return;
      case 'expirado':
        setErro('Código expirado (validade de 15 min). Gere um novo no celular.');
        return;
      case 'limite':
        setErro('Muitas tentativas. Aguarde um minuto e tente de novo.');
        return;
      case 'invalido':
        setErro('Código inválido ou já usado.');
        return;
      case 'erro':
        setErro(r.mensagem);
        return;
    }
  }

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        background: cor.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <form
        onSubmit={enviar}
        style={{
          background: cor.branco,
          border: `1px solid ${cor.linhaForte}`,
          padding: '28px 30px',
          width: 380,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div style={{ fontFamily: fonte.condensada, fontWeight: 600, fontSize: 24, lineHeight: 1 }}>
          v<span style={{ color: cor.azul }}>med</span>
        </div>
        <div style={{ fontSize: 14, color: cor.muted, lineHeight: 1.5 }}>
          Digite o código de 6 dígitos que apareceu no seu celular.
        </div>
        <input
          inputMode="numeric"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          style={{
            fontFamily: fonte.mono,
            fontSize: 30,
            letterSpacing: '.24em',
            textAlign: 'center',
            padding: '10px 12px',
            border: `1px solid ${cor.linha}`,
            outlineColor: cor.azul,
            color: cor.texto,
          }}
        />
        {erro && (
          <div
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: cor.ambarTexto,
              background: cor.ambarBg,
              border: `1px solid ${cor.ambarBorda}`,
              padding: '8px 10px',
            }}
          >
            {erro}
          </div>
        )}
        <button
          type="submit"
          disabled={!valido || carregando}
          style={{
            ...btnPrimario,
            height: 44,
            fontSize: 15,
            opacity: !valido || carregando ? 0.5 : 1,
            cursor: !valido || carregando ? 'default' : 'pointer',
          }}
        >
          {carregando ? 'Abrindo…' : 'Abrir rascunho'}
        </button>
      </form>
    </div>
  );
}
